import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';

import {
  EntireRangeUnit,
  type NullableDateFilterState,
  useEntireDatePresetFilter,
} from '@/features/Commons/EntireDatePresetFilter';
import type { StructuredFilterQueryInterface } from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import service from '@/services';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import type { FeedbackListItemWithBlock } from '@/types/feedback';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { PREDICTION_STATE } from '@/types/taxonomyConst';
import { getTimezoneDayjs } from '@/utils/time';

import predictionStateUtils from '../components/Topic2Detail/PredictionStateDisplay/utils';
import useTopicDetailQueries, {
  FeedbackListTarget,
} from '../components/Topic2Detail/queries/useTopicDetailQueries';
import taxonomyUtils from '../utils/taxonomy';
import TaxonomyContext from './TaxonomyContext';
import {
  buildTopicDetailSearchParams,
  parseTopicDetailSearchParams,
} from './topic-detail-search-params';

type TopicDetailContextT = {
  feedbackList?: PaginatedData<FeedbackListItemWithBlock>[];
  count: number;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  removeItem: (items: ClassifiedFeedbackUnit[]) => void;

  feedbackSearchValue: string;
  onKeyEnterFeedbackSearch: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  dateRangeString: NullableDateRangeT | null;
  dateState: NullableDateFilterState;
  onDateFilterChange: (dateState: NullableDateFilterState) => void;

  filterQueryFormats: StructuredFilterQueryFormatT[];
  onFilterSetChange: (
    filterQueryFormats: StructuredFilterQueryFormatT[],
  ) => void;
  clearFilter: () => void;

  hasFilter: boolean;
  initFeedbacSearchValue: () => void;

  selectedTab: FeedbackListTarget;
  selectTab: (tab: FeedbackListTarget) => void;

  childTopics: Topic2TreeItemT<TOPIC_2_TYPE>[];
  topic?: Topic2T<TOPIC_2_TYPE>;
  isPrediction: boolean;

  feedbackCount: number;
  unlinkedFeedbackCount: number;
  currentPredictionState: PredictionProgressPayload;
  rootTopics: string[];
};

type BaseProps = {
  topicAlias?: string;
  taxonomyAlias: string;
  enabled?: boolean;
};

const TopicDetailContext = createContext<TopicDetailContextT>({
  feedbackList: undefined,
  count: 0,
  hasNextPage: false,
  isLoading: false,
  isFetchingNextPage: false,
  fetchNextPage: () => {},
  refetch: () => {},
  removeItem: () => {},

  feedbackSearchValue: '',
  filterQueryFormats: [],
  dateRangeString: null,
  dateState: {
    dateRange: [getTimezoneDayjs(), getTimezoneDayjs()],
    rangeUnit: EntireRangeUnit.Entire,
    presetItem: null,
  },
  onDateFilterChange: () => {},
  onFilterSetChange: () => {},
  clearFilter: () => {},
  hasFilter: false,
  onKeyEnterFeedbackSearch: () => {},
  initFeedbacSearchValue: () => {},
  selectedTab: FeedbackListTarget.TOTAL,
  selectTab: () => {},
  childTopics: [],
  isPrediction: false,
  feedbackCount: 0,
  unlinkedFeedbackCount: 0,
  topic: undefined,
  currentPredictionState: {
    prediction_state: PREDICTION_STATE.IDLE,
  },
  rootTopics: [],
});

export const TopicFeedbackListProvider = ({
  children,
  topicAlias,
  taxonomyAlias,
}: {
  children: React.ReactNode;
  topicAlias?: string;
  taxonomyAlias: string;
}) => {
  const { taxonomy } = useContext(TaxonomyContext);
  const { parseStructuredFilter } = useStructuredFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const topicTreeMap = useTaxonomyStore((state) => state.topicTreeMap);
  const urlState = useMemo(
    () => parseTopicDetailSearchParams(new URLSearchParams(searchParamsString)),
    [searchParamsString],
  );
  const topicQuery = useTopicDetailQueries.useTopicDetailQuery({
    topicAlias: topicAlias,
    taxonomyAlias,
    enabled: !!topicAlias,
  });
  const [feedbackList, setFeedbackList] = useState<
    PaginatedData<FeedbackListItemWithBlock>[]
  >([]);
  const [count, setCount] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const filterSetString = JSON.stringify(urlState.filterSet ?? null);
  const initialFilterFormats = useMemo(
    () =>
      urlState.filterSet ? parseStructuredFilter(urlState.filterSet) : [],
    [filterSetString, parseStructuredFilter, urlState.filterSet],
  );
  const {
    filterQueryFormats,
    onFilterSetChange: setFilterQueryFormats,
    clearFilter: clearStructuredFilter,
    resetFilter,
  }: StructuredFilterQueryInterface = useStructuredFilterQuery(
    undefined,
    initialFilterFormats,
  );

  const hasNextPage = !!cursor;

  const [feedbackSearchValue, setFeedbackSearchValue] = useState(
    urlState.searchTerm,
  );

  const currentPredictionState: PredictionProgressPayload = useMemo(() => {
    try {
      if (topicAlias) {
        const topic = taxonomyUtils.getTaxonomyTopic(
          topicTreeMap,
          taxonomyAlias,
          topicAlias,
        );
        if (!topic) throw new Error('Topic not found');
        return {
          prediction_state: topic.prediction_state,
          progress_rate: topic.progress_rate,
        };
      }
      if (!taxonomy) throw new Error('Taxonomy not found');
      return {
        prediction_state: taxonomy.prediction_state,
        progress_rate: taxonomy.progress_rate,
      };
    } catch {
      return {
        prediction_state: PREDICTION_STATE.IDLE,
      };
    }
  }, [taxonomy, taxonomyAlias, topicTreeMap, topicAlias]);

  const rootTopics = useMemo(() => {
    return topicTreeMap?.[taxonomyAlias]?.root || [];
  }, [taxonomyAlias, topicTreeMap]);

  const childTopics: Topic2TreeItemT<TOPIC_2_TYPE>[] = useMemo(() => {
    if (topicAlias) {
      const childTopics = topicQuery.data
        ?.children_detail as Topic2TreeItemT<TOPIC_2_TYPE>[];
      if (!childTopics) return [];
      return childTopics;
    }
    return rootTopics.reduce((acc, t) => {
      try {
        const topic = taxonomyUtils.getTaxonomyTopic(
          topicTreeMap,
          taxonomyAlias,
          t,
        );
        if (topic) {
          acc.push(topic);
        }
      } catch (e) {
        return acc;
      }
      return acc;
    }, [] as Topic2TreeItemT<TOPIC_2_TYPE>[]);
  }, [
    rootTopics,
    taxonomyAlias,
    topicAlias,
    topicQuery.data?.children_detail,
    topicTreeMap,
  ]);

  const isPrediction = predictionStateUtils.isPrediction(
    currentPredictionState.prediction_state,
  );

  const feedbackCountQuery = useTopicDetailQueries.useFeedbackCountQuery({
    topicAlias: topicAlias,
    taxonomyAlias,
    enabled: !isPrediction,
  });

  const unlinkedFeedbackCountQuery =
    useTopicDetailQueries.useUnlinkedFeedbackCountQuery({
      topicAlias: topicAlias,
      taxonomyAlias,
      enabled: childTopics && childTopics.length > 0,
    });

  const [selectedTab, setSelectedTab] = useState<FeedbackListTarget>(
    urlState.selectedTab,
  );

  const {
    dateState,
    setDateState,
    onDateFilterChange: setTopicDetailDateState,
    dateInit,
  } = useEntireDatePresetFilter({
    initialValue: urlState.dateState,
  });

  const dateRangeString = useMemo(() => {
    if (!dateState.dateRange) return null;
    return {
      start_date: dateState.dateRange[0].toJSON(),
      end_date: dateState.dateRange[1].toJSON(),
    };
  }, [dateState.dateRange]);

  const syncSearchParams = (
    next: Partial<{
      filterQueryFormats: StructuredFilterQueryFormatT[];
      searchTerm: string;
      selectedTab: FeedbackListTarget;
      dateState: NullableDateFilterState;
    }>,
  ) => {
    const nextSearchParams = buildTopicDetailSearchParams({
      currentSearchParams: searchParams,
      filterSet:
        structuredFilterUtils.buildStructuredFilter(
          next.filterQueryFormats ?? filterQueryFormats,
        ) || undefined,
      searchTerm: next.searchTerm ?? feedbackSearchValue,
      selectedTab: next.selectedTab ?? selectedTab,
      dateState: next.dateState ?? dateState,
    });

    if (nextSearchParams.toString() === searchParamsString) {
      return;
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const onFilterSetChange = (formats: StructuredFilterQueryFormatT[]) => {
    setFilterQueryFormats(formats);
    syncSearchParams({ filterQueryFormats: formats });
  };

  const clearFilter = () => {
    clearStructuredFilter();
    syncSearchParams({ filterQueryFormats: [] });
  };

  const onKeyEnterFeedbackSearch = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const value = e.currentTarget.value;
    setFeedbackSearchValue(value);
    e.preventDefault();
    e.currentTarget.blur();
    syncSearchParams({ searchTerm: value });
  };

  const initFeedbacSearchValue = () => {
    setFeedbackSearchValue('');
    syncSearchParams({ searchTerm: '' });
  };

  const selectTab = (tab: FeedbackListTarget) => {
    setSelectedTab(tab);
    syncSearchParams({ selectedTab: tab });
  };

  const onDateFilterChange = (nextDateState: NullableDateFilterState) => {
    setTopicDetailDateState(nextDateState);
    syncSearchParams({ dateState: nextDateState });
  };

  const fetchFeedbackList = async (pageParam: null | string) => {
    const targetAlias = topicAlias || taxonomyAlias;

    const getFetcher = (
      params: BaseProps & {
        target: FeedbackListTarget;
      },
    ) => {
      if (params.topicAlias) {
        if (params.target === FeedbackListTarget.TOTAL)
          return service.taxonomy.fetchTopicFeedbackList;
        if (params.target === FeedbackListTarget.UNLINKED)
          return service.taxonomy.fetchTopicUnclassifiedFeedbackList;
      } else {
        if (params.target === FeedbackListTarget.TOTAL)
          return service.taxonomy.fetchTaxonomyFeedbackList;
        if (params.target === FeedbackListTarget.UNLINKED)
          return service.taxonomy.fetchTaxonomyUnclassifiedFeedbackList;
      }
      throw new Error('Invalid case');
    };

    let fetcher = getFetcher({
      topicAlias,
      taxonomyAlias,
      target: selectedTab,
    });

    if (!dateInit) return [];
    if (!pageParam) {
      setFeedbackList([]);
      setIsLoading(true);
    } else {
      setIsFetchingNextPage(true);
    }
    const structuredFilter =
      structuredFilterUtils.buildStructuredFilter(filterQueryFormats);

    const data = await fetcher(targetAlias, {
      query: {
        cursor: pageParam,
      },
      payload: {
        ...structuredFilter,
        ...structuredFilterUtils.buildDateFilter(dateRangeString),
        [FEEDBACK_COLUMNS.SNIPPET]:
          structuredFilterUtils.buildSingleValue(feedbackSearchValue),
      },
    });

    const cursor =
      (data.feedbacks &&
        data.feedbacks?.[data.feedbacks?.length - 1]?.paginate_token) ||
      null;

    setFeedbackList(
      pageParam
        ? (prev) => [...prev, ...(data.feedbacks || [])]
        : data.feedbacks || [],
    );
    setCount(data.count);
    setCursor(cursor);
    setIsLoading(false);
    setIsFetchingNextPage(false);
  };

  const removeItem = (items: ClassifiedFeedbackUnit[]) => {
    const flattenBlockIds = items.flatMap((item) => item.block_id_list || []);
    setFeedbackList((prev) => {
      return prev.reduce((acc, feedback) => {
        const newFeedback = {
          ...feedback,
          blocks: feedback.blocks?.filter(
            (block) => !flattenBlockIds.includes(block.alias),
          ),
        };
        if (newFeedback.blocks?.length === 0) {
          return acc;
        }
        acc.push(newFeedback);
        return acc;
      }, [] as PaginatedData<FeedbackListItemWithBlock>[]);
    });
  };

  const fetchNextPage = async () => {
    setIsFetchingNextPage(true);
    await fetchFeedbackList(cursor);
    setIsFetchingNextPage(false);
  };

  const refetch = async () => {
    feedbackCountQuery.refetch();
    unlinkedFeedbackCountQuery.refetch();
    await fetchFeedbackList(null);
  };

  useDeepCompareEffect(() => {
    fetchFeedbackList(null);
  }, [
    dateInit,
    JSON.stringify(dateRangeString),
    feedbackSearchValue,
    filterQueryFormats,
    selectedTab,
    taxonomyAlias,
    topicAlias,
  ]);

  useEffect(() => {
    setFeedbackSearchValue((prev) =>
      prev === urlState.searchTerm ? prev : urlState.searchTerm,
    );
  }, [urlState.searchTerm]);

  useEffect(() => {
    setSelectedTab((prev) =>
      prev === urlState.selectedTab ? prev : urlState.selectedTab,
    );
  }, [urlState.selectedTab]);

  useEffect(() => {
    if (
      JSON.stringify(filterQueryFormats) === JSON.stringify(initialFilterFormats)
    ) {
      return;
    }

    resetFilter(initialFilterFormats);
  }, [filterQueryFormats, initialFilterFormats, resetFilter]);

  useEffect(() => {
    const currentDateStateString = JSON.stringify({
      rangeUnit: dateState.rangeUnit,
      presetItem: dateState.presetItem,
      dateRange: dateState.dateRange?.map((date) => date.toJSON()) ?? null,
    });
    const nextDateStateString = JSON.stringify({
      rangeUnit: urlState.dateState.rangeUnit,
      presetItem: urlState.dateState.presetItem,
      dateRange:
        urlState.dateState.dateRange?.map((date) => date.toJSON()) ?? null,
    });

    if (currentDateStateString === nextDateStateString) {
      return;
    }

    setDateState(urlState.dateState);
  }, [dateState, setDateState, urlState.dateState]);

  const hasFilter = useMemo(() => {
    if (feedbackSearchValue) return true;
    if (filterQueryFormats.length > 0) return true;
    if (dateState.rangeUnit !== EntireRangeUnit.Entire) return true;
    return false;
  }, [filterQueryFormats?.length, dateState.rangeUnit, feedbackSearchValue]);

  return (
    <TopicDetailContext.Provider
      value={{
        feedbackList,
        count,
        hasNextPage,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        refetch,
        removeItem,
        filterQueryFormats,
        dateRangeString,
        feedbackSearchValue,
        dateState,
        onDateFilterChange,
        onFilterSetChange,
        clearFilter,
        hasFilter,
        onKeyEnterFeedbackSearch,
        initFeedbacSearchValue,
        selectedTab,
        selectTab,
        childTopics,
        isPrediction,
        feedbackCount: feedbackCountQuery.data?.count || 0,
        unlinkedFeedbackCount: unlinkedFeedbackCountQuery.data?.count || 0,
        topic: topicQuery.data,
        currentPredictionState,
        rootTopics,
      }}
    >
      {children}
    </TopicDetailContext.Provider>
  );
};

export const useTopicDetailContext = () => useContext(TopicDetailContext);
